import { html, LitElement, css} from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { VoyageStyle } from '../../voyage-style/src/voyage-style';
import { VesselPlanningModel } from '../../../src/models/vessel-planning-model';

const timeScales = [
    { ms: 60*60*1000, pixels: 67, type: "hour" }, // one hour per unit
    { ms: 24*60*60*1000, pixels: 67, type: "day" }, // one day per unit
    { ms: 7*24*60*60*1000, pixels: 138, type: "week" } // one week per unit
];

const monthTexts = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec"
];

@customElement('voyage-time-slider')
export class VoyageTimeSlider extends LitElement
{
    @property({type: Number}) scale: number = 0;
    @property({type: String}) mode: string = "";
    @property({type: String}) planningScheduleId: string = "";

    constructor()
    {
        super();

        this._newBounds = this._newBounds.bind(this);
        this._renderCanvas = this._renderCanvas.bind(this);
        this.requestUpdate = this.requestUpdate.bind(this);
        this._updateColors = this._updateColors.bind(this);
    }

    get focusTime()
    {
        return this._focusTime;
    }

    set focusTime(value)
    {
        if (!this._dragging)
        {
            this._focusTime = value;
        }
    }

    static get styles() {
        return [
            css`
            :host {
                position: relative;
                height: 100%;
                margin: 0;
                padding: 0;
            }
            
            canvas {
                height: 100%;
                width: 100%;
                margin: 0;
                cursor: ew-resize;
            }

            #currentTimeIndicator {
                position: absolute;
                top: -6px;
                width: 8px;
                height: 8px;
                border-radius: 4px;
                background-color: var(--timeline-current-time-color, green);
            }

            #focusIndicator {
                position: absolute;
                bottom: calc(100% + 5px);
            }

            .focusIndDelta {
                display: flex;
                flex-direction: row;
                box-shadow: 0 0 5px 0 rgba(0,0,0,0.25);
            }

            .focusIndBlank {
                background: var(--primary-blue-05, blue);
                height: 6px;
                width: 18px;
                border-radius: 3px 3px 0 0;
            }

            button {
                display: block;
                background: var(--primary-blue-05, blue);
                border-radius: 2px;
                border: none;
                height: 26px;
                width: 62px;
                outline: none;
                padding: 0;
                color: var(--neutrals-grey-10, white);
                font-family: MaerskText;
                font-size: 12px;
            }

            button > svg {
                width: 12px;
                fill: currentColor;
                margin-left: 5px;
            }

            .offsetText {
                display: flex;
                align-items: center;
                margin-left: 1px;
                height: 26px;
                font-family: MaerskText;
                font-size: 12px;
                color: var(--neutrals-grey-10, white);
                background: var(--primary-blue-05, blue);
                text-align: center;
            }

            .offsetText > div {
                margin-left: 10px;
                margin-right: 10px;
            }

            .focusArrow {
                position: absolute;
                left: 0px;
                height: 6px;
                width: 18px;
                top: 100%;
                background: var(--primary-blue-05, blue);
            }

            .focusArrow[has-content] {
                left: 54px;
            }

            .focusArrow:after {
                position: absolute;
                content: "";
                transform: rotate(45deg);
                left: 2.64px;
                width: 12.73px;
                height: 12.73px;
                background: var(--primary-blue-05, blue);
            }
        `];
    }

    render()
    {
        requestAnimationFrame(this._renderCanvas);
        const dateNow = Date.now();

        return html`
            <canvas width=${this._cwidth} height=${this._cheight}
                    @pointerdown=${this._onPointerDown}
                    @pointermove=${this._onPointerMove}
                    @pointerup=${this._onPointerUp}></canvas>
            
            <div id="currentTimeIndicator" ?hidden=${this._focusTime <= dateNow || this._timeToX(dateNow) < -1}
                style="left:${this._timeToX(dateNow) - 4}px"></div>
            
            <div id="focusIndicator" style="left:${
                this._timeToX(this._focusTime > dateNow ? this._focusTime : dateNow) - (this._focusTime > dateNow ? 63 : 9)
                }px">
                ${ this._focusTime > dateNow ? html`
                <div class="focusIndDelta">
                    <button id="resetButton" @click=${this._resetTime}>RESET ${SvgIcons.undo}</button>
                    <div class="offsetText"><div>${this._getDeltaTimeText()}</div></div>
                </div>` : html`
                <div class="focusIndBlank"></div>`}
                <div class="focusArrow" ?has-content=${this._focusTime > dateNow}></div>
            </div>
        `;
    }

    firstUpdated()
    {
        this._canvas =  this.shadowRoot!.querySelector('canvas');
        this._newBounds();
    }

    connectedCallback()
    {
        super.connectedCallback();

        // On loading the proper MaerskText font, make sure to re-render.
        // TBD: Should we wait rendering until the font is there?
        // NOTE: This currently only works in Chrome 60+ & FF 41+
        // @ts-ignore
        document.fonts.addEventListener('loadingdone', this.requestUpdate);

        // NOTE: We will only get window resize events - but ResizeObserver on the element
        // gave problems in test.  As long as we use the element in a context that will resize on window
        // resize, it will be fine.
        window.addEventListener('resize', this._newBounds);

        VoyageStyle.addEventListener('styles-changed', this._updateColors);
        this._updateColors();
    }

    disconnectedCallback()
    {
        super.disconnectedCallback();

        // @ts-ignore
        document.fonts.removeEventListener('loadingdone', this.requestUpdate);

        window.removeEventListener('resize', this._newBounds);

        VoyageStyle.removeEventListener('styles-changed', this._updateColors);
    }

    @property({attribute: false}) private _focusTime: number = -1;
    @property({attribute: false}) private _cwidth: number = 0;
    @property({attribute: false}) private _cheight: number = 0;
    private _dragging: boolean = false;
    private _dragStartX: number = 0;
    private _canvas: HTMLCanvasElement|null = null;
    private _bgColor: string = "black";
    private _tickColor: string = "white";
    private _textColor: string = "white";
    private _currentTimeColor: string = "green";
    private _focusTimeColor: string = "blue";

    private _xToTime(x: number)
    {
        const msPerPixel = timeScales[this.scale].ms/timeScales[this.scale].pixels;
        const startTime = Math.max(this._focusTime, Date.now()) - timeScales[this.scale].ms;
        return startTime + Math.floor(msPerPixel * (x - timeScales[this.scale].pixels));
    }

    private _timeToX(time: number)
    {
        const msPerPixel = timeScales[this.scale].ms/timeScales[this.scale].pixels;
        const startTime = Math.max(this._focusTime, Date.now()) - timeScales[this.scale].ms;
        return (time - startTime)/msPerPixel;
    }

    _updateColors()
    {
        // Read out colors
        const computedStyle = getComputedStyle(this);
        this._bgColor = computedStyle.getPropertyValue("--timeline-bg-color") || this._bgColor;
        this._tickColor = computedStyle.getPropertyValue("--timeline-tick-color") || this._tickColor;
        this._textColor = computedStyle.getPropertyValue("--timeline-fg-color") || this._textColor;
        this._currentTimeColor = computedStyle.getPropertyValue("--timeline-current-time-color") || this._currentTimeColor;
        this._focusTimeColor = computedStyle.getPropertyValue("--primary-blue-05") || this._focusTimeColor;
        this.requestUpdate();
    }

    _getIndicatorLeft()
    {
        const x = this._timeToX(Math.max(this._focusTime, Date.now()));
    }

    _onPointerDown(evt: PointerEvent)
    {
        this._dragging = true;
        this._dragStartX = evt.clientX;
        (<HTMLElement>evt.target).setPointerCapture(evt.pointerId);
    }

    _onPointerMove(evt: PointerEvent)
    {
        if (this._dragging)
        {
            this._updateFocusTime(this._dragStartX - evt.clientX);
            this._dragStartX = evt.clientX;
        }
    }

    _onPointerUp(evt: PointerEvent)
    {
        if (this._dragging)
        {
            this._dragging = false;
            this._updateFocusTime(this._dragStartX - evt.clientX, true);
            (<HTMLElement>evt.target).releasePointerCapture(evt.pointerId);
        }
    }

    _updateFocusTime(deltaX: number, emitEvent?: boolean)
    {
        const currTime = Math.max(this._focusTime, Date.now());
        const msPerPixel = timeScales[this.scale].ms/timeScales[this.scale].pixels;
        const newTime = currTime + deltaX*msPerPixel;
        this._setFocusTime(newTime, emitEvent);
    }

    _setFocusTime(newTime: number, emitEvent?: boolean)
    {
        if (newTime <= Date.now())
        {
            this._focusTime = -1;
        }
        else
        {
            this._focusTime = newTime;
        }

        if (emitEvent)
        {
            this.dispatchEvent(new CustomEvent('focustime-update', {
                detail: { focustime: this._focusTime > 0 ? this._focusTime : Date.now() },
                bubbles: true,
                composed: true
            }));
        }
    }

    async _resetTime()
    {
        if(this.mode === "suggestedRoutes")
        {
           const currentPlan = await VesselPlanningModel!.getCurrentPlan(this.planningScheduleId);
            
            if (currentPlan && currentPlan!.etd > Date.now()) // if future route
            {
                this._setFocusTime(currentPlan!.etd, true)
            }
            else {
                this._setFocusTime(-1, true) // if current route
            }
        }
        else 
        {
            this._setFocusTime(-1, true)
        }
    }

    _newBounds()
    {
        if (this._canvas)
        {
            const cr = this._canvas.getBoundingClientRect();

            this._cwidth = Math.ceil(cr.width);
            this._cheight = Math.ceil(cr.height);
        }
    }

    _renderCanvas()
    {
        const ctx = this._canvas!.getContext("2d")!;

        ctx.fillStyle = this._bgColor;
        ctx.fillRect(0, 0, this._cwidth, this._cheight);

        // Draw ticks
        let startTime = Math.max(this._focusTime, Date.now());
        let startTickTime = 0;
        const date = new Date(startTime);
        // Round downwards/backwards to nearest whole hour/day/week
        date.setUTCMilliseconds(0);
        date.setUTCSeconds(0);
        date.setUTCMinutes(0);
        if (timeScales[this.scale].type != "hour")
        {
            date.setUTCHours(0);
            if (timeScales[this.scale].type === "week")
            {
                // Round down to monday (day 1)
                const day = date.getUTCDay();
                const subtractDays = day >= 1 ? day - 1 : 6;
                date.setTime(date.getTime() - subtractDays*24*60*60*1000);
            }
        }

        // Start drawing ticks one unit before current (rounded down) time
        startTickTime = date.getTime() - timeScales[this.scale].ms;

        ctx.strokeStyle = this._tickColor;
        ctx.fillStyle = this._textColor;
        ctx.lineWidth = 1;
        ctx.font = '12px MaerskText';
        ctx.textAlign = 'center';
        ctx.textBaseline = "top";
        let tickTime = startTickTime;
        let x = this._timeToX(tickTime);
        do {
            const tickDate = new Date(tickTime);
            const writeDate = timeScales[this.scale].type === "hour" && tickDate.getUTCHours() === 0;
            ctx.beginPath();
            ctx.moveTo(x, 0);
            let y = 29;
            if (writeDate)
            {
                // Need some extra space for the date text
                y = 25;
            }
            ctx.lineTo(x, y);
            ctx.stroke();

            switch (timeScales[this.scale].type)
            {
                case "hour":
                    if (writeDate)
                    {
                        ctx.fillText(`${monthTexts[tickDate.getUTCMonth()]} ${tickDate.getUTCDate()}`, x, y);
                        y += 14;
                    }
                    else
                    {
                        y += 6;
                    }
                    ctx.fillText(`${tickDate.getUTCHours()}:00`, x, y);
                break;

                case "day":
                    y += 6;
                    ctx.fillText(`${monthTexts[tickDate.getUTCMonth()]} ${tickDate.getUTCDate()}`, x, y);
                    break;

                case "week":
                    y += 6;
                    ctx.fillText(`Week ${this._getWeek(tickDate)}`, x, y);
                    break;
            }

            tickTime += timeScales[this.scale].ms;
            x = this._timeToX(tickTime);
        } while(x <= this._cwidth);

        // Draw focus time
        x = this._timeToX(startTime);
        ctx.strokeStyle = this._focusTimeColor;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, this._cheight);
        ctx.stroke();

        // Draw current time if
        // a - focus time is set (ie. different from current time)
        // b - it is in the visible part
        if (this._focusTime > Date.now())
        {
            x = this._timeToX(Date.now());
            if (x >= -1)
            {
                ctx.strokeStyle = this._currentTimeColor;
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x, this._cheight);
                ctx.stroke();
            }
        }
    }

    // Returns the ISO8601 week number - shamelessly stolen from https://www.epochconverter.com/weeknumbers
    _getWeek(date: Date)
    {
        const target = new Date(date);
        const dayNr = (date.getUTCDay() + 6) % 7;
        target.setUTCDate(target.getUTCDate() - dayNr + 3);
        const firstThursday = target.getTime();
        target.setUTCMonth(0, 1);
        if (target.getUTCDay() != 4)
        {
            target.setUTCMonth(0, 1 + ((4 - target.getUTCDay()) + 7) % 7);
        }
        return 1 + Math.ceil((firstThursday - target.getTime()) / 604800000);
    }

    _getDeltaTimeText()
    {
        const delta = this._focusTime - Date.now();
        const minutes = delta/(60*1000);
        const hours = minutes/60;
        if (hours < 1)
        {
            return "+" + `${Math.floor(minutes)}`.padStart(2, "0") + "m";
        }
        const days = hours/24;
        if (days < 1)
        {
            return "+" + `${Math.floor(hours)}`.padStart(2, "0") + "h";
        }
        
        return `+${Math.floor(days)}d ` + `${Math.floor(hours)%24}`.padStart(2, "0") + "h";
    }
}
